import { Filter } from "../models/Filter";
import { GetByFiltersArguments } from "../models/GetByFiltersArguments";
import { FilterOperators } from "../constants/FilterOperators";
import { FiltersExpressions } from "../constants/FiltersExpressions";
import { SortDirections } from "../constants/SortDirections";
import { ObjectFieldDataTypes } from "../constants/ObjectFieldDataTypes";
import { FiltersArgumentsBuilder } from "../builders/FiltersArgumentsBuilder";
import { QueryFilter } from "../models/QueryFilter";
import { ObjectField } from "../models/ObjectField";
import { AuthorizedUser } from "../models/AuthorizedUser";

import Moment from "moment";

type LogitudeLazyLoadParameters = { PageIndex: number, PageSize: number, SortBy: string | null, SortDirection: SortDirections | null, Filters: Filter[] };

export class CommonFiltersArguments {

    static getDatatableFilters(datatableLazyLoad: any, additionalFilters: Filter[] = []): GetByFiltersArguments {
        let filters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.filters);
        let quickSearchFilters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.quickSearchFilters);
        let sortByField = datatableLazyLoad.sortField;
        let sortDirection = datatableLazyLoad.sortOrder === -1 ? SortDirections.Descending : SortDirections.Ascending;
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .addFilters(filters)
            .addFilters(additionalFilters)
            .filtersExpression(FiltersExpressions.And)
            .addQuickSearchFilters(quickSearchFilters)
            .pageIndex(datatableLazyLoad.first)
            .pageSize(datatableLazyLoad.rows)
            .sortBy(sortByField)
            .sortDirection(sortDirection)
            .build();
        return getByFiltersArguments;
    }

    static getDatatableFiltersForUserQuery(datatableLazyLoad: any, queryFilters: QueryFilter[]): GetByFiltersArguments {
        let filters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.filters);
        let quickSearchFilters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.quickSearchFilters);
        let filtersFromQuery: Filter[] = this.getFiltersFromQuery(queryFilters);
        filters = filters.concat(filtersFromQuery);
        let sortByField = datatableLazyLoad.sortField;
        let sortDirection = datatableLazyLoad.sortOrder === -1 ? SortDirections.Descending : SortDirections.Ascending;
        let getByFiltersArguments: FiltersArgumentsBuilder = new FiltersArgumentsBuilder()
            .addFilters(filters)
            .filtersExpression(FiltersExpressions.And)
            .addQuickSearchFilters(quickSearchFilters)
            .pageIndex(datatableLazyLoad.first)
            .pageSize(datatableLazyLoad.rows)
            .sortBy(sortByField)
            .sortDirection(sortDirection);
        return getByFiltersArguments.build();
    }

    static getDropdownFilters(sortByField: string, sortDirections: SortDirections): GetByFiltersArguments {
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .sortBy(sortByField)
            .sortDirection(sortDirections)
            .getAll()
            .build();
        return getByFiltersArguments;
    }

    static getAutoCompleteFilters(filterFields: string[], searchTerm: string, filterOperators: FilterOperators): GetByFiltersArguments {
        let filtersArgumentsBuilder: FiltersArgumentsBuilder = new FiltersArgumentsBuilder();
        filterFields.forEach(filterField => {
            filtersArgumentsBuilder = filtersArgumentsBuilder.addFilter(filterField, searchTerm, filterOperators);
        });
        let getByFiltersArguments: GetByFiltersArguments = filtersArgumentsBuilder
            .filtersExpression(FiltersExpressions.Or)
            .pageIndex(0)
            .pageSize(100)
            .build();
        return getByFiltersArguments;
    }

    static getUserQueriesFilters(objectTableCode: string): GetByFiltersArguments {
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .addFilter("ObjectTableCode", objectTableCode, FilterOperators.Equals)
            .filtersExpression(FiltersExpressions.And)
            .sortBy("IndexOrder")
            .sortDirection(SortDirections.Ascending)
            .getAll()
            .build();
        return getByFiltersArguments;
    }

    static getUserTenantObjectFieldsFilters(objectTableCode: string, canFilter: boolean | null = null): GetByFiltersArguments {
        let filtersArgumentsBuilder: FiltersArgumentsBuilder = new FiltersArgumentsBuilder()
            .addFilter("ObjectTableCode", objectTableCode, FilterOperators.Equals)
            .filtersExpression(FiltersExpressions.And)
            .sortBy("Name")
            .sortDirection(SortDirections.Ascending)
            .getAll();
        if (canFilter !== null) {
            filtersArgumentsBuilder = filtersArgumentsBuilder.addFilter("CanFilter", (canFilter ? "true" : "false"), FilterOperators.Equals);
        }
        return filtersArgumentsBuilder.build();
    }

    static getUserTenantCustomObjectFieldsFilters(): GetByFiltersArguments {
        let filtersArgumentsBuilder: FiltersArgumentsBuilder = new FiltersArgumentsBuilder()
            .addFilter("IsCustom", "true", FilterOperators.Equals)
            .addFilter("ObjectTableCode", "Task", FilterOperators.Equals)
            .filtersExpression(FiltersExpressions.And)
            .sortBy("DataTypeCode")
            .sortDirection(SortDirections.Ascending)
            .getAll();

        return filtersArgumentsBuilder.build();
    }

    static getPartnerFieldPermissionsFilters(partnerTypeCode: string, objectTableCode: string): GetByFiltersArguments {
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .filtersExpression(FiltersExpressions.And)
            .addFilter("PartnerTypeCode", partnerTypeCode, FilterOperators.Equals)
            .addFilter("ObjectTableCode", objectTableCode, FilterOperators.Equals)
            .getAll()
            .build();
        return getByFiltersArguments;
    }

    static filterObjectFieldByName(tenant: number, objectTableCode: string, searchTerm: string): GetByFiltersArguments {
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .tenant(tenant)
            .addFilter("Name", searchTerm.trim(), FilterOperators.Contains)
            .addFilter("ObjectTableCode", objectTableCode, FilterOperators.Equals)
            .filtersExpression(FiltersExpressions.And)
            .sortBy("Id")
            .sortDirection(SortDirections.Ascending)
            .build();
        return getByFiltersArguments;
    }

    static getDoneScreenHeaderFieldFilters(objectTableCode: string): GetByFiltersArguments {
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .addFilter("EntityCode", objectTableCode, FilterOperators.Equals)
            .filtersExpression(FiltersExpressions.And)
            .sortDirection(SortDirections.Ascending)
            .getAll()
            .build();
        return getByFiltersArguments;
    }

    static getGroupByFieldFilters(objectField: ObjectField, scrollEvent: any, datatableLazyLoad: any, queryFilters: QueryFilter[], firstPageSize: number): { FirstPageSize: number, Field: string, IsCustom: boolean, DataType: string | null, ApiQueryFilter: GetByFiltersArguments } {
        let filters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.filters);
        let quickSearchFilters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.quickSearchFilters);
        let filtersFromQuery: Filter[] = this.getFiltersFromQuery(queryFilters);
        filters = filters.concat(filtersFromQuery);
        let groupByField = (objectField.IsCustom ? (objectField.ObjectTableCode + "CustomField." + objectField.Code) : objectField.Code);
        let groupFieldDataType = this.getFilterType(objectField);
        let sortDirection = datatableLazyLoad.sortOrder === -1 ? SortDirections.Descending : SortDirections.Ascending;
        let filtersArgumentsBuilder: FiltersArgumentsBuilder = new FiltersArgumentsBuilder()
            .addFilters(filters)
            .filtersExpression(FiltersExpressions.And)
            .addQuickSearchFilters(quickSearchFilters)
            .pageIndex(scrollEvent.first)
            .pageSize(scrollEvent.rows)
            .sortBy(datatableLazyLoad.sortField)
            .sortDirection(sortDirection);
        return { FirstPageSize: firstPageSize, Field: groupByField, IsCustom: objectField.IsCustom, DataType: groupFieldDataType, ApiQueryFilter: filtersArgumentsBuilder.build() };
    }

    static getGroupCountFilters(datatableLazyLoad: any, groupFilterMetaData: any, queryFilters: QueryFilter[]): GetByFiltersArguments {
        let filters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.filters);
        let quickSearchFilters = this.buildFiltersFromDatatableFilterMetadata(datatableLazyLoad.quickSearchFilters);
        let groupFilter = this.buildFiltersFromDatatableFilterMetadata(groupFilterMetaData);
        let filtersFromQuery: Filter[] = this.getFiltersFromQuery(queryFilters);
        filters = filters.concat(filtersFromQuery).concat(groupFilter);
        let filtersArgumentsBuilder: FiltersArgumentsBuilder = new FiltersArgumentsBuilder()
            .addFilters(filters)
            .filtersExpression(FiltersExpressions.And)
            .addQuickSearchFilters(quickSearchFilters)
            .pageIndex(0)
            .pageSize(0);
        return filtersArgumentsBuilder.build();
    }

    static getAllDocumentTypesFilters(): GetByFiltersArguments {
        let filtersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .getAll()
            .build();
        return filtersArguments;
    }

    static filterDocumentTypeByName(searchTerm: string): GetByFiltersArguments {
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .addFilter("Name", searchTerm.trim(), FilterOperators.Contains)
            .filtersExpression(FiltersExpressions.And)
            .sortBy("Id")
            .sortDirection(SortDirections.Ascending)
            .build();
        return getByFiltersArguments;
    }

    static getTaskTypeCustomComponentsFilters(objectTableCode: string): GetByFiltersArguments {
        let getByFiltersArguments: GetByFiltersArguments = new FiltersArgumentsBuilder()
            .addFilter("ObjectTableCode", objectTableCode, FilterOperators.Equals)
            .filtersExpression(FiltersExpressions.And)
            .getAll()
            .build();
        return getByFiltersArguments;
    }

    static getShipmentsFilters(lazyLoadParameters: LogitudeLazyLoadParameters): GetByFiltersArguments {
        let filtersArgumentsBuilder: FiltersArgumentsBuilder = new FiltersArgumentsBuilder()
            .addFilters(lazyLoadParameters.Filters)
            .filtersExpression(FiltersExpressions.And)
            .pageIndex(lazyLoadParameters.PageIndex)
            .pageSize(lazyLoadParameters.PageSize);
        if (lazyLoadParameters.SortBy !== null) {
            filtersArgumentsBuilder.sortBy(lazyLoadParameters.SortBy);
        }
        if (lazyLoadParameters.SortDirection !== null) {
            filtersArgumentsBuilder.sortDirection(lazyLoadParameters.SortDirection);
        }
        return filtersArgumentsBuilder.build();
    }

    static getInActiveTaskTypeFilter() : Filter{
        return {
            Operator: "Equals",
            Property: "Active",
            Value: "true",
            FilterDataType: null,
            IsCustomFilter: false
          };
    }

    private static buildFiltersFromDatatableFilterMetadata(datatableLazyLoadFilters: any): Filter[] {
        let filters: Filter[] = [];
        if (datatableLazyLoadFilters === null || datatableLazyLoadFilters === undefined) {
            return filters;
        }
        let filterFields = Object.getOwnPropertyNames(datatableLazyLoadFilters);
        filterFields.forEach(filterField => {
            let filterFieldMatchMode = datatableLazyLoadFilters[filterField].matchMode;
            let filterFieldValue = datatableLazyLoadFilters[filterField].value;
            let isCustomFilter = datatableLazyLoadFilters[filterField].isCustom;
            let filterDataType = datatableLazyLoadFilters[filterField].dataType;
            if (filterFieldValue !== undefined && filterFieldValue !== null && filterFieldValue !== "") {
                let filter: Filter = {
                    Property: filterField.indexOf("*") === -1 ? filterField : filterField.split('*')[0],
                    Value: filterFieldValue === "$EmptyFilterValue$" ? null : filterFieldValue.toString(),
                    Operator: filterFieldMatchMode,
                    IsCustomFilter: isCustomFilter,
                    FilterDataType: filterDataType
                };
                filters.push(filter);
            }
        });
        return filters;
    }

    private static getFiltersFromQuery(queryFilters: QueryFilter[]): Filter[] {
        let filtersFromQuery: Filter[] = queryFilters.map((queryFilter) => {
            return {
                Property: (queryFilter.ObjectField?.IsCustom ?
                    (queryFilter.ObjectField.ObjectTableCode + "CustomField." + queryFilter.ObjectFieldCode) :
                    queryFilter.ObjectFieldCode),
                Value: (queryFilter.Condition !== null && (queryFilter.ObjectField?.DataTypeCode === ObjectFieldDataTypes.Date || queryFilter.ObjectField?.DataTypeCode === ObjectFieldDataTypes.DateTime) ?
                    this.formatFilterConditionValue(queryFilter) :
                    this.formatFilterValue(queryFilter)),
                Operator: queryFilter.Operator,
                IsCustomFilter: queryFilter.ObjectField?.IsCustom!,
                FilterDataType: this.getFilterType(queryFilter.ObjectField!)
            };
        });
        return filtersFromQuery;
    }

    private static formatFilterValue(queryFilter: QueryFilter): string | null {
        let filterValue = queryFilter.Value;
        if (filterValue === null &&
            (queryFilter.ObjectField?.DataTypeCode === ObjectFieldDataTypes.Integer || queryFilter.ObjectField?.DataTypeCode === ObjectFieldDataTypes.Decimal)) {
            return "0";
        }
        else if (filterValue === null &&
            (queryFilter.ObjectField?.DataTypeCode === ObjectFieldDataTypes.Boolean)) {
            return "false";
        }
        else {
            switch (filterValue) {
                case "@LoggedUserId":
                    let loggedUserId = AuthorizedUser.getId();
                    let loggedUserIdValue = (loggedUserId === undefined || loggedUserId === null) ? "-1" : loggedUserId.toString();
                    return loggedUserIdValue;
                default:
                    return filterValue;
            }
        }
    }

    private static formatFilterConditionValue(queryFilter: QueryFilter): string | null {
        let isDateTime = queryFilter.ObjectField?.DataTypeCode === ObjectFieldDataTypes.DateTime;
        if (queryFilter.Condition === "Today+") {
            return this.getToday(Number(queryFilter.Value), isDateTime);
        }
        else if (queryFilter.Condition === "Today-") {
            return this.getToday(-Number(queryFilter.Value), isDateTime);
        }
        else {
            return null;
        }
    }

    private static getToday(diffDays: number, isDateTime: boolean): string {
        let today = Moment().add(diffDays, "days").toDate();
        let formattedDate = today.getFullYear() + "-" + (today.getMonth() + 1) + "-" + today.getDate();
        // if (isDateTime) {
        //     formattedDate = formattedDate + " " + today.getHours() + ":" + today.getMinutes();
        // }
        return formattedDate;
    }

    private static getFilterType(objectField: ObjectField) {
        if (!objectField.IsCustom) {
            return null;
        }
        if (objectField.DataTypeCode === ObjectFieldDataTypes.PickList) {
            return ObjectFieldDataTypes.String;
        }
        if (objectField.DataTypeCode === ObjectFieldDataTypes.Lookup) {
            return objectField.LookupObjectTable?.IsClosedTable ? ObjectFieldDataTypes.String : ObjectFieldDataTypes.Integer;
        }
        return objectField.DataTypeCode;
    }
}
